<template>
  <div class="eye-exam-receipt w-full" :class="[`er-${size}`, { 'er-a6-tight': size === 'a6' && showSection('contact') }]" dir="ltr">
    <div v-if="showSection('exam') || showSection('prism')" class="er-row">
      <div v-if="showSection('exam')" class="er-block">
        <div class="er-title" :style="titleStyle">
          <span>Visual Acuity &amp; IOP</span>
          <span v-if="arabic" class="er-title-ar">حدة البصر وضغط العين</span>
        </div>
        <div v-if="exam.vaCatfordDrum" class="er-catford" :class="{ 'er-catford-ar': arabic }">
          <span v-if="arabic" dir="rtl">تم قياسه باستخدام Catford drum</span>
          <span v-else>Measured with a Catford drum</span>
        </div>
        <table class="er-table">
          <thead :style="headStyle">
          <tr>
            <th class="er-eye-col"></th>
            <th>VA WOG</th>
            <th>VA WG</th>
            <th>IOP <span class="er-unit">mmHg</span></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="eye in eyes" :key="eye">
            <td class="er-eye">{{ eye }}</td>
            <td>{{ value(`vaWithoutGlasses${eye}`) }}</td>
            <td>{{ value(`vaWithGlasses${eye}`) }}</td>
            <td>{{ value(`iop${eye}Status`) || value(`iop${eye}`) }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-if="showSection('prism')" class="er-block">
        <div class="er-title" :style="titleStyle">
          <span>Prism</span>
          <span v-if="arabic" class="er-title-ar">البريزم</span>
        </div>
        <table class="er-table">
          <thead :style="headStyle">
          <tr>
            <th class="er-eye-col"></th>
            <th>PRISM <span class="er-unit">Δ</span></th>
            <th>BASE</th>
            <th>AXIS</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="eye in eyes" :key="eye">
            <td class="er-eye">{{ eye }}</td>
            <td>{{ value(`prism${eye}`) }}</td>
            <td>{{ value(`prism${eye}BASE`) }}</td>
            <td>{{ axis(`prism${eye}AXIS`) }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showSection('distance') || showSection('reading')" class="er-row">
      <div v-for="rx in rxSections" :key="rx.key" class="er-block" :class="{ 'er-hidden': !showSection(rx.key) }">
        <template v-if="showSection(rx.key)">
          <div class="er-title" :style="titleStyle">
            <span>{{ rx.title }} <span class="er-abbr">{{ rx.abbr }}</span></span>
            <span v-if="arabic" class="er-title-ar">{{ rx.arabic }}</span>
          </div>
          <table class="er-table er-rx">
            <thead :style="headStyle">
            <tr>
              <th class="er-eye-col"></th>
              <th>
                <span v-if="rx.key === 'reading' && readingIsAdd" class="er-add-head">ADD</span>
                <template v-else>SPH</template>
              </th>
              <th>CYL</th>
              <th>AXIS</th>
              <th>PD</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(eye, index) in eyes" :key="eye">
              <td class="er-eye">{{ eye }}</td>
              <td>{{ rx.key === 'reading' ? readingSPH(eye) : diopter(`distance${eye}SPH`) }}</td>
              <td>{{ diopter(`${rx.key}${eye}CYL`) }}</td>
              <td>{{ axis(`${rx.key}${eye}AXIS`) }}</td>
              <td v-if="index === 0" rowspan="2" class="er-pd">{{ value(`${rx.key}ODIPD`) }}</td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>

    <div v-if="showSection('contact')" class="er-block" :class="{ 'er-half': contactColumns.length === 3 && size !== 'a6' }">
      <div class="er-title" :style="titleStyle">
        <span>Contact Lenses <span class="er-abbr">CL</span></span>
        <span v-if="arabic" class="er-title-ar">العدسات اللاصقة</span>
      </div>
      <table :key="contactColumns.length" class="er-table">
        <thead :style="headStyle">
        <tr>
          <th class="er-eye-col"></th>
          <th v-for="column in contactColumns" :key="column.key">{{ column.label }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="eye in eyes" :key="eye">
          <td class="er-eye">{{ eye }}</td>
          <td v-for="column in contactColumns" :key="column.key">{{ column.format(`contact${eye}${column.key}`) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="notes && size !== 'a6'" class="er-block">
      <div class="er-title" :style="titleStyle">
        <span>Notes</span>
        <span v-if="arabic" class="er-title-ar">الملاحظات</span>
      </div>
      <div class="er-notes" dir="auto">{{ notes }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, type PropType} from 'vue'
import Examination, {
  eyeExamFieldsHaveValues,
  eyeExamSectionHasValues,
  formatDiopter,
  nearSPH,
  type EyeExamSection
} from '@/models/customer/Examination'

const props = defineProps({
  examination: {
    type: Object as PropType<Examination | null>,
    default: null
  },
  size: {
    type: String as PropType<'a4' | 'a5' | 'a6'>,
    default: 'a4'
  },
  brandingColor: {
    type: String,
    default: '#14919B'
  },
  // Show an Arabic subtitle under each English section title
  arabic: {
    type: Boolean,
    default: false
  },
  hideEmptySections: {
    type: Boolean,
    default: true
  },
  // Print the near SPH as distance SPH + ADD instead of the ADD itself
  addReadingToDistance: {
    type: Boolean,
    default: false
  }
})

const eyes = ['OD', 'OS'] as const

const rxSections = [
  {key: 'distance', title: 'Distance', abbr: 'DV', arabic: 'نظارة البعيد'},
  {key: 'reading', title: 'Near', abbr: 'NV', arabic: 'نظارة القراءة'}
] as const

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const exam = computed(() => (props.examination ?? {}) as Record<string, any>)

const notes = computed(() => (exam.value.notes || '').trim())

const readingIsAdd = computed(() => !!exam.value.readingAdd && !props.addReadingToDistance)

const titleStyle = computed(() => ({borderColor: props.brandingColor}))

const headStyle = computed(() => ({background: tint(props.brandingColor, 0.12)}))

function tint(hex: string, alpha: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return `rgba(20, 145, 155, ${alpha})`
  const n = parseInt(match[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

function showSection(section: EyeExamSection): boolean {
  // A6 is too small for Prism; its Contact Lenses table only has SPH / CYL / AXIS
  if (props.size === 'a6' && section === 'prism') return false
  if (section === 'prism' && !eyeExamSectionHasValues(props.examination, 'prism')) return false
  if (!props.hideEmptySections) return true
  if (props.size === 'a6' && section === 'contact') return eyeExamFieldsHaveValues(props.examination, A6_CONTACT_FIELDS)
  return eyeExamSectionHasValues(props.examination, section)
}

function raw(field: string): unknown {
  const v = exam.value[field]
  return v == null || v === '' || (typeof v === 'number' && isNaN(v)) ? null : v
}

function value(field: string): string {
  const v = raw(field)
  return v == null ? '' : String(v)
}

function diopter(field: string): string {
  const v = raw(field)
  return v == null ? '' : formatDiopter(Number(v))
}

function axis(field: string): string {
  const v = raw(field)
  return v == null ? '' : `${v}°`
}

const A6_CONTACT_FIELDS = ['contactODSPH', 'contactODCYL', 'contactODAXIS', 'contactOSSPH', 'contactOSCYL', 'contactOSAXIS']

// Power always prints; Keratometry and Lens Fit only when one of their fields was measured (never on A6)
const contactGroups = [
  [
    {key: 'SPH', label: 'SPH', format: diopter},
    {key: 'CYL', label: 'CYL', format: diopter},
    {key: 'AXIS', label: 'AXIS', format: axis}
  ],
  [
    {key: 'K1', label: 'K1', format: value},
    {key: 'K2', label: 'K2', format: value},
    {key: 'KAVE', label: 'K-AVE', format: value}
  ],
  [
    {key: 'BC', label: 'B.C.', format: value},
    {key: 'DIA', label: 'DIA', format: value}
  ]
]

const contactColumns = computed(() => contactGroups
    .filter((group, index) => index === 0 || (props.size !== 'a6'
        && group.some(column => eyes.some(eye => raw(`contact${eye}${column.key}`) != null))))
    .flat())

function readingSPH(eye: 'OD' | 'OS'): string {
  const add = raw(`reading${eye}SPH`) as number | null
  if (add == null) return ''
  if (exam.value.readingAdd && props.addReadingToDistance) {
    return formatDiopter(nearSPH(raw(`distance${eye}SPH`) as number | null, add))
  }
  return formatDiopter(add)
}
</script>

<style scoped>
.er-catford {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5em;
  margin: -0.15em 0 0.25em;
  padding-inline-start: 0.6em;
  font-size: 0.7em;
  font-weight: 700;
  color: #111;
  letter-spacing: 0;
}

.er-catford-ar {
  justify-content: flex-end;
}

.er-catford span {
  white-space: nowrap;
}

.eye-exam-receipt {
  @apply px-8 mt-8 flex flex-col;
  gap: 1rem;
}

.er-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.5rem;
}

.er-hidden {
  visibility: hidden;
}

/* Power-only Contact Lenses lines up with the left-hand boxes above it */
.er-half {
  width: calc((100% - 1.5rem) / 2);
}

.er-title {
  @apply flex items-baseline justify-between font-bold text-[#444] mb-1.5;
  border-left: 4px solid;
  padding-left: 0.5rem;
  letter-spacing: 0.02em;
}

.er-title-ar {
  @apply font-bold text-[#777];
  letter-spacing: 0;
  font-size: 0.85em;
  direction: rtl;
}

.er-title > span {
  white-space: nowrap;
}

/* Half-width A5 columns: keep "English + Arabic" titles on one line */
.er-a5 .er-title {
  gap: 0.5rem;
  font-size: 0.85em;
}

.er-abbr {
  @apply text-[#888];
  font-size: 0.8em;
  margin-left: 0.25em;
}

.er-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  text-align: center;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  overflow: hidden;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.er-table th {
  @apply text-[#555] font-bold;
  font-size: 0.8em;
  padding: 4px 4px;
  border-bottom: 1px solid #d4d4d4;
}

.er-table td {
  font-weight: 700;
  padding: 6px 4px;
  height: 2.1em;
  color: #111;
}

.er-table tbody tr:first-child td {
  border-bottom: 1px solid #ececec;
}

.er-table td + td,
.er-table th + th {
  border-left: 1px solid #ececec;
}

.er-eye-col {
  width: 3.2em;
}

.er-eye {
  @apply text-[#686868];
}

.er-pd {
  vertical-align: middle;
  border-bottom: none !important;
}

.er-rx td {
  font-size: 1.1em;
}

/* ADD heading is highlighted so it isn't misread as the near SPH */
.er-add-head {
  display: inline-block;
  padding: 0 0.6em;
  border-radius: 999px;
  background: #dcdcdc;
  color: #111;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.er-unit {
  @apply font-normal text-[#999];
  font-size: 0.85em;
}

.er-notes {
  @apply rounded-md p-3 whitespace-pre-wrap font-bold;
  background: #f4f8fc;
  border: 1px solid #dbe6f1;
  unicode-bidi: plaintext;
  text-align: start;
  max-height: 60mm;
  overflow: hidden;
  /* Bold like the customer name in the header, slightly smaller */
  font-size: 1.19em;
  line-height: 1.3;
  color: #111;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* A5 is the A4 page zoomed to 0.7, so the text gets a little bigger to stay readable */
.er-a5 {
  font-size: 1.12em;
}

.er-a5 .er-notes {
  max-height: 70mm;
  /* Undo the A5 body enlargement so notes match the Retinoscopy notes */
  font-size: calc(1.19em / 1.12);
}

/* A6 (105 x 148 mm): VA/IOP, DV and NV only, stacked full-width with large text */
.er-a6 {
  @apply mt-4;
  padding-inline: 5.5mm;
  gap: 8px;
  font-size: 10px;
}

.er-a6 .er-row {
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
}

.er-a6 .er-hidden {
  display: none;
}

.er-a6 .er-title {
  margin-bottom: 3px;
  border-left-width: 3px;
  padding-left: 4px;
  font-size: 11px;
  letter-spacing: 0;
}

.er-a6 .er-table {
  border-radius: 4px;
}

.er-a6 .er-table th {
  font-size: 9px;
  padding: 3px 2px;
}

.er-a6 .er-table td {
  font-size: 13px;
  padding: 2px;
  height: 25px;
}

.er-a6 .er-rx td {
  font-size: 14px;
}

/* A6 with the Contact Lenses table: tighter rows so 4 sections fit one page */
.er-a6-tight {
  @apply mt-3;
  gap: 5px;
}

.er-a6-tight .er-row {
  gap: 5px;
}

.er-a6-tight .er-title {
  margin-bottom: 2px;
  font-size: 10px;
}

.er-a6-tight .er-table td {
  height: 20px;
  line-height: 1.15;
}

.er-a6-tight .er-table th {
  padding: 2px;
}

.er-a6 .er-eye-col {
  width: 3em;
}

.er-a6 .er-unit {
  display: none;
}
</style>
